/**
 * Defines color scheme for using with surface items
 */
export interface ColorScheme {
    /**
     * The css color for accessible text on a schemePrimary background
     * DEPRECATED, will be removed in future version. Use accessibleText.schemePrimaryBackground instead
     */
    text: string;

    /**
     * The css color for accessible text on a schemeLighter background
     * DEPRECATED, will be removed in future version. Use accessibleText.schemeLighterBackground instead
     */
    textAlt: string;

    schemeDarker: string;
    schemeDark: string;
    schemeDarkAlt: string;
    schemePrimary: string;
    schemeSecondary: string;
    schemeTertiary: string;
    schemeLight: string;
    schemeLighter: string;
    schemeLighterAlt: string;
    colorName: () => string;

    /**
     * The css color for accessible test on the given backgrounds
     */
    accessibleText?: AccessibleTextScheme;
}

export interface AccessibleTextScheme {
    schemePrimaryBackground: string;
    schemeLighterBackground: string;
    msColorWhiteBackground: string;
    msColorNeutralLighterBackground: string;
}

export enum AccessibleTextSchemeBackground {
    schemePrimary,
    schemeLighter,
    msColorWhite,
    msColorNeutralLighter,
}

export default ColorScheme;

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties with the schemePrimary scheme color.
 */
export function colorSchemeToCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemePrimary,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

/**
 * Function to convert ColorScheme into border CSS properties with the schemePrimary scheme color.
 */
export function colorSchemeToCssEmpty(scheme: ColorScheme): React.CSSProperties {
    return {
        borderColor: scheme.schemePrimary,
        color: 'transparent',
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToDarkCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeDarkAlt,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToInvertCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeLighter,
        color: scheme.accessibleText
            ? scheme.accessibleText.schemeLighterBackground
            : scheme.textAlt,
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToMediumCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeLight,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

/**
 * Function to convert ColorScheme into color and backgroundColor CSS properties for the calendar surface with the schemeLight scheme color.
 */
export function colorSchemeToLightCss(scheme: ColorScheme): React.CSSProperties {
    return {
        backgroundColor: scheme.schemeLighter,
        color: scheme.accessibleText ? scheme.accessibleText.schemePrimaryBackground : scheme.text,
    };
}

export function getAccessibleTextColor(
    scheme: ColorScheme,
    background: AccessibleTextSchemeBackground = AccessibleTextSchemeBackground.msColorWhite
): string {
    switch (background) {
        case AccessibleTextSchemeBackground.schemePrimary:
            return scheme.accessibleText
                ? scheme.accessibleText.schemePrimaryBackground
                : scheme.schemeLighterAlt;
        case AccessibleTextSchemeBackground.schemeLighter:
            return scheme.accessibleText
                ? scheme.accessibleText.schemeLighterBackground
                : scheme.schemePrimary;
        case AccessibleTextSchemeBackground.msColorNeutralLighter:
            return scheme.accessibleText
                ? scheme.accessibleText.msColorNeutralLighterBackground
                : scheme.schemePrimary;
        case AccessibleTextSchemeBackground.msColorWhite:
        default:
            return scheme.accessibleText
                ? scheme.accessibleText.msColorWhiteBackground
                : scheme.schemePrimary;
    }
}
